// PageDescription: 지역별, 역주변별 비급여 가격비교 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React, { useMemo } from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import NonBenefitHomeTemplate from '@/components/templates/nonBenefit/home/NonBenefitHomeTemplate';
import useGetBannerAdExchange from '@/hooks/ad/useGetBannerAdExchange';
import useSearchKoreaAddressV2 from '@/hooks/hospital/search/useSearchKoreaAddressV2';
import useNonBenefitHomeViewModel from '@/hooks/nonBenefit/useNonBenefitHomeViewModel';
import useNonBenefitMedicinesForSeo from '@/hooks/nonBenefit/useNonBenefitMedicinesForSeo';
import useNonBenefitPriceTotalSummary from '@/hooks/nonBenefit/useNonBenefitPriceTotalSummary';
import useSearchNonF2fDoctorsForSeoByUser from '@/hooks/nonBenefit/useSearchNonF2fDoctorsForSeoByUser';
import useGetKeywordSearchMagazineInfinite from '@/hooks/search/magazine/useGetKeywordSearchMagazineInfinite';
import useGetKeywordQuestionCardInfinite from '@/hooks/search/qna/useGetKeywordQuestionCardInfinite';
import { AdPageType } from '@/models/ad/list';
import { Region } from '@/models/nonBenefit';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { MagazineType } from '@/types/magazine';
import { NonBenefitHomeQueries } from '@/types/nonBenefit';
import { nonBenefitSeoLdJson } from '@/utils/format';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';

const NonBenefitHomePages = ({ client }: CustomPageProps) => {
  const viewModel = useNonBenefitHomeViewModel(client);
  const { treatmentMedicineUnitId, regionCode } = viewModel.queries as NonBenefitHomeQueries;

  const title = `${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} 최저가 가격 ${viewModel.selectedMedicine.medicineName} 2025`;
  const description = `${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} ${viewModel.selectedMedicine.medicineName} 성지 병원 가격 비교 - ${viewModel.selectedMedicine.medicineProductName}`;
  const canonical = `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.NON_BENEFIT.HOME.path}${addQueryString({
    treatmentMedicineUnitId,
    regionCode,
  })}`;

  const nonBenefitLdJson = useMemo(() => {
    return nonBenefitSeoLdJson({
      doctors: viewModel.nonBenefitDoctors,
      address: viewModel.selectedAddressTitle,
      faqs: viewModel.faqs,
      canonical,
    });
  }, []);

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          title,
          description: description,
          url: canonical,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAIN_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(nonBenefitLdJson) }} />
      </Head>
      <NonBenefitHomeTemplate client={client} viewModel={viewModel} />
    </>
  );
};

NonBenefitHomePages.Layout = DefaultLayout;
export default NonBenefitHomePages;

const SEOUL_REGION_CODE = '11';
export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const queryClient = new QueryClient();
  const queries = ctx.query as NonBenefitHomeQueries;

  const nonBenefitMedicines = await useNonBenefitMedicinesForSeo.fetchQuery(queryClient);

  /* 쿼리가 없는 경우 리다이렉트 */
  if (!queries.treatmentMedicineUnitId || !queries.regionCode) {
    const treatmentMedicineUnitId = queries.treatmentMedicineUnitId ?? nonBenefitMedicines.medicines[0].units[0].id;

    const regionCode = queries.regionCode ?? SEOUL_REGION_CODE;

    return {
      redirect: {
        permanent: false,
        destination:
          ROUTES.NON_BENEFIT.HOME.path +
          addQueryString({
            treatmentMedicineUnitId: treatmentMedicineUnitId,
            regionCode: regionCode,
          }),
      },
    };
  }

  /* 색인 이슈로 잘못된 treatmentMedicineUnitId가 유입되고 있어 매칭되는 medicine이 없으면 리다이렉트 */
  const medicine = nonBenefitMedicines.findMedicineByQueryTreatmentMedicineUnitId(queries.treatmentMedicineUnitId);

  if (!medicine) {
    const treatmentMedicineUnitId = nonBenefitMedicines.medicines[0].units[0].id;

    return {
      redirect: {
        permanent: false,
        destination:
          ROUTES.NON_BENEFIT.HOME.path +
          addQueryString({
            treatmentMedicineUnitId: treatmentMedicineUnitId,
            regionCode: queries.regionCode,
          }),
      },
    };
  }

  const queryRegion = new Region(queries.regionCode);

  await Promise.allSettled([
    useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.NonBenefitDetail, ctx.client),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: null,
      siGunGuCode: null,
    }),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: queryRegion.excludeSejongSiDoRegionCode(),
      siGunGuCode: null,
    }),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: queryRegion.regionCodes.siDoCode,
      siGunGuCode: queryRegion.regionCodes.siGunGuCode,
    }),
    useNonBenefitPriceTotalSummary.prefetchQuery(queryClient, medicine.id, queries.regionCode),
    useSearchNonF2fDoctorsForSeoByUser.prefetchQuery(queryClient, {
      regionCode: queries.regionCode,
      treatmentMedicineUnitId: queries.treatmentMedicineUnitId,
      limit: 3,
      page: 1,
    }),
    useGetKeywordQuestionCardInfinite.prefetchQuery(queryClient, {
      keyword: medicine.treatment.name,
      size: 5,
    }),
    useGetKeywordSearchMagazineInfinite.prefetchQuery(queryClient, {
      keyword: medicine.treatment.name,
      size: 7,
      contentType: MagazineType.FAQ,
    }),
    useGetKeywordSearchMagazineInfinite.prefetchQuery(queryClient, {
      keyword: medicine.treatment.name,
      size: 7,
      contentType: MagazineType.ARTICLE,
    }),
  ]);

  return {
    props: {
      queries,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
});
